import React, { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import _ from "lodash";
import { latLonToMeters, locationsAreEqual } from "../../../util/Geometry";
import { useSelector, useStore } from "react-redux";
import { LineSegment } from "./LineSegment";
import { Line } from "@react-three/drei";
import { distanceToRouteCoordinate } from "components/util/GeometryUtilsV2";
import { routeGeomSelector } from "redux/selectors";

const METERS_BETWEEN_POINTS = 5;

const currentPositionSelector = (state) => state.playlist.position.coords;
const tunnelWidthSelector = (state) => _.get(state.playlist.overlays, ["Tunnel", "width"], 4000) / 1000;
const tunnelHeightSelector = (state) => _.get(state.playlist.overlays, ["Tunnel", "height"], 4000) / 1000;
const tunnelLengthSelector = (state) => _.get(state.playlist.overlays, ["Tunnel", "length"], 5);
const currentSessionTagsSelector = (state) => _.get(state.sessions[state.playlist.data.routeID], "tags", []);

const playlistDistanceSelector = (state) => state.playlist.position.playlistDistance;

function ThreeDTunnel() {
    // Trigger updates on redux position state changes for this component only
    const store = useStore();

    const ref = useRef();
    const currentPositionRef = useRef(currentPositionSelector(store.getState()));

    const tunnelHalfWidth = useSelector(tunnelWidthSelector) / 2;
    const tunnelHeight = useSelector(tunnelHeightSelector);
    const tunnelLength = useSelector(tunnelLengthSelector);
    const currentSessionTags = useSelector(currentSessionTagsSelector);
    const routeGeometryData = useSelector(routeGeomSelector);
    const currentPlaylistDistance = useSelector(playlistDistanceSelector);

    const sessionHasDirectionTag = useMemo(() => {
        return _.includes(currentSessionTags, "Forward") || _.includes(currentSessionTags, "Backward");
    }, [currentSessionTags]);

    const sessionIsBackward = useMemo(() => {
        return _.includes(currentSessionTags, "Backward");
    }, [currentSessionTags]);

    const { routeGeometry, zeroPoint } = useMemo(() => {
        console.log("Generating tunnel geometry...");

        const routeGeometry = [];
        let zeroPoint = null;
        let lastCoordinate = null;

        const coordinateData = _.get(routeGeometryData, ["distance_to_coordinate"], []);

        let targetDistance = currentPlaylistDistance;
        for (let i = 0; i <= tunnelLength; i++) {
            const coordinate = distanceToRouteCoordinate(targetDistance, coordinateData);
            if (zeroPoint === null) {
                zeroPoint = coordinate;
            }

            if (!locationsAreEqual(lastCoordinate, coordinate)) {
                const metersPos = latLonToMeters(zeroPoint, coordinate);
                routeGeometry.push(metersPos);
            }
            lastCoordinate = coordinate;

            if (sessionIsBackward) {
                targetDistance -= METERS_BETWEEN_POINTS;
            } else {
                targetDistance += METERS_BETWEEN_POINTS;
            }
        }
        return { routeGeometry, zeroPoint };
    }, [currentPlaylistDistance, routeGeometryData, sessionIsBackward, tunnelLength]);

    const { blRail, brRail, tlRail, trRail, pillars } = useMemo(() => {
        const blRail = [];
        const brRail = [];
        const tlRail = [];
        const trRail = [];
        const pillars = [];

        if (routeGeometry.length < 2) {
            //do nothing
        } else {
            for (let i = 0; i < routeGeometry.length; i++) {
                let o0, o1, p;
                if (i === 0) {
                    o0 = p = routeGeometry[i];
                    o1 = routeGeometry[i + 1];
                } else {
                    o0 = routeGeometry[i - 1];
                    o1 = p = routeGeometry[i];
                }
                const angle = Math.atan2(o1[1] - o0[1], o1[0] - o0[0]);
                const sinAngle = Math.sin(angle);
                const cosAngle = Math.cos(angle);
                const xOffset = cosAngle * tunnelHalfWidth;
                const zOffset = -sinAngle * tunnelHalfWidth;

                const bottomLeft = [-p[1] - xOffset, 0, -p[0] - zOffset];
                const bottomRight = [-p[1] + xOffset, 0, -p[0] + zOffset];
                const topLeft = [-p[1] - xOffset, tunnelHeight, -p[0] - zOffset];
                const topRight = [-p[1] + xOffset, tunnelHeight, -p[0] + zOffset];

                blRail.push(bottomLeft);
                brRail.push(bottomRight);
                tlRail.push(topLeft);
                trRail.push(topRight);

                pillars.push(bottomLeft, topLeft, topLeft, topRight, topRight, bottomRight);
            }
        }

        return { blRail, brRail, tlRail, trRail, pillars };
    }, [routeGeometry, tunnelHalfWidth, tunnelHeight]);

    useEffect(() => {
        return store.subscribe(() => {
            const currentPosition = currentPositionSelector(store.getState());
            if (!locationsAreEqual(currentPosition, currentPositionRef.current)) {
                currentPositionRef.current = currentPosition;
            }
        });
    }, [store]);

    useFrame(() => {
        if (currentPositionRef.current && zeroPoint) {
            const positionOffset = latLonToMeters(zeroPoint, currentPositionRef.current);
            if (ref.current) {
                ref.current.position.set(positionOffset[1], 0, positionOffset[0]);
            }
        }
    });

    const blLine = useMemo(() => {
        return (
            <Line
                points={blRail}
                color={"red"}
            />
        );
    }, [blRail]);

    const brLine = useMemo(() => {
        return (
            <Line
                points={brRail}
                color={"red"}
            />
        );
    }, [brRail]);

    const tlLine = useMemo(() => {
        return (
            <Line
                points={tlRail}
                color={"red"}
            />
        );
    }, [tlRail]);

    const trLine = useMemo(() => {
        return (
            <Line
                points={trRail}
                color={"red"}
            />
        );
    }, [trRail]);

    const pillarsLine = useMemo(() => {
        return (
            <LineSegment
                points={pillars}
                color={"white"}
            />
        );
    }, [pillars]);

    if (sessionHasDirectionTag && routeGeometry.length) {
        return (
            <group ref={ref}>
                {blLine}
                {brLine}
                {tlLine}
                {trLine}
                {pillarsLine}
            </group>
        );
    } else {
        return null;
    }
}

export default ThreeDTunnel;
